import { createApi, fetchBaseQuery, } from '@reduxjs/toolkit/query/react';
import camelcaseKeys from 'camelcase-keys';
import accessoriesTransformResponse from '../apiResponseTransformers/accessories';
import legacyInventoryTransformResponse from '../apiResponseTransformers/legacyInventory';
import transformPaymentResponse, { transformMultiplePaymentsResponse, } from '../apiResponseTransformers/payment';
import transformLegacyProtectionResponse from '../apiResponseTransformers/legacyProtection';
import transformSummaryResponse from '../apiResponseTransformers/summary';
import transformOffersResponse from '../apiResponseTransformers/offers';
import transformWarrantiesResponse from '../apiResponseTransformers/warranties';
import transformAuthResponse from '../apiResponseTransformers/userAuth';
const MULTIPLE_PAYMENTS_CHUNK_SIZE = 10;
// Split multiple payment requests into chunks of 10
const chunkArray = (array) => {
    const result = [];
    for (let i = 0; i < array.length; i += MULTIPLE_PAYMENTS_CHUNK_SIZE) {
        result.push(array.slice(i, i + MULTIPLE_PAYMENTS_CHUNK_SIZE));
    }
    return result;
};
// API slice for legacy Honda REST API, e.g., 'api.honda.ca', 'uat-api.honda.ca'
const hondaRestApi = createApi({
    reducerPath: 'hondaRestApi',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['saved_build'],
    endpoints: builder => ({
        getAccessories: builder.query({
            query: ({ url, params }) => ({ url, params }),
            transformResponse: accessoriesTransformResponse,
        }),
        getPayment: builder.query({
            query: ({ url, body, languageCode }) => ({
                url,
                method: 'POST',
                body,
                headers: { 'Accept-Language': languageCode },
                data: { AcceptLanguage: languageCode },
            }),
            transformResponse: transformPaymentResponse,
        }),
        getMultiplePayments: builder.query({
            async queryFn(arg, _queryApi, _extraOptions, baseQuery) {
                // Just return the single payment if it fits in a single request
                if (arg.body.length <= MULTIPLE_PAYMENTS_CHUNK_SIZE) {
                    const result = (await baseQuery({
                        url: arg.url,
                        method: 'POST',
                        body: arg.body,
                    }));
                    if (result.data) {
                        return { data: transformMultiplePaymentsResponse(result.data, undefined, arg) };
                    }
                }
                // Split the payments into chunks and make multiple requests
                const chunks = chunkArray(arg.body);
                const results = await Promise.all(chunks.map(chunk => baseQuery({
                    url: arg.url,
                    method: 'POST',
                    body: chunk,
                })));
                const data = results.reduce((acc, result) => {
                    if (result.data) {
                        acc.push(...result.data);
                    }
                    return acc;
                }, []);
                return { data: transformMultiplePaymentsResponse(data, undefined, arg) };
            },
        }),
        getInventoryStatus: builder.query({
            query: ({ url, body }) => ({ url, method: 'POST', body }),
            transformResponse: legacyInventoryTransformResponse,
        }),
        getProtection: builder.query({
            query: ({ url }) => ({ url }),
            transformResponse: transformLegacyProtectionResponse,
        }),
        getBuildPriceSummary: builder.query({
            query: ({ url, body, languageCode }) => ({
                url,
                method: 'POST',
                body,
                headers: { 'Accept-Language': languageCode },
                data: { AcceptLanguage: languageCode },
            }),
            transformResponse: transformSummaryResponse,
        }),
        getOffers: builder.query({
            query: ({ hostname, transmissionIds, provinceCode, languageCode, productLineCode }) => `${hostname}/models/${productLineCode}/Live/BuildAndPrice/offers/${transmissionIds}/province/${provinceCode}?AcceptLanguage=${languageCode}`,
            transformResponse: transformOffersResponse,
        }),
        getOffersWithLoyalty: builder.query({
            query: ({ hostname, transmissionIds, provinceCode, languageCode, productLineCode }) => `${hostname}/models/${productLineCode}/Live/BuildAndPrice/offers/${transmissionIds}/province/${provinceCode}/withloyalty?AcceptLanguage=${languageCode}`,
            transformResponse: transformOffersResponse,
        }),
        getWarranties: builder.query({
            query: ({ hostname, transmissionIds, provinceCode, languageCode, productLineCode, bodyStyle, term, annualKmAllowance, paymentMethod, }) => {
                const baseQuery = `${hostname}/models/${productLineCode}/Live/transmissions/${transmissionIds}/warranties?AcceptLanguage=${languageCode}&provinceCode=${provinceCode}&bodyStyle=${bodyStyle}&term=${term}&paymentMethod=${paymentMethod}`;
                if (annualKmAllowance) {
                    return `${baseQuery}&kmAllowance=${annualKmAllowance}`;
                }
                return baseQuery;
            },
            transformResponse: transformWarrantiesResponse,
        }),
        getSavedBuild: builder.query({
            query: ({ url, headers }) => ({ url, headers }),
            providesTags: (_result, _error, arg) => [{ type: 'saved_build', id: arg.buildId }],
            transformResponse: (response) => camelcaseKeys(response, { deep: true }) || {},
        }),
        getUserAuth: builder.query({
            query: ({ url }) => ({ url, credentials: 'include' }),
            transformResponse: transformAuthResponse,
        }),
    }),
});
export const hondaRestApiReducer = hondaRestApi.reducer;
export const hondaRestApiMiddleware = hondaRestApi.middleware;
export const { useGetInventoryStatusQuery, useGetAccessoriesQuery, useGetPaymentQuery, useGetMultiplePaymentsQuery, useGetProtectionQuery, useGetBuildPriceSummaryQuery, useGetOffersQuery, useGetOffersWithLoyaltyQuery, useGetWarrantiesQuery, useGetSavedBuildQuery, useGetUserAuthQuery, } = hondaRestApi;
export default hondaRestApi;
